import React from "react";
import { Box, Typography } from "@mui/material";
import { useNotification } from "../../../context/notification-provider";
import { useDialogs } from "../../dialog/use-dialog";
import { FileRecord } from "../types";
import {
  handleDeleteFile,
  handleDownloadFile,
  handleReprocessFile,
} from "../utils/file-actions-handlers";

interface UseFileActionsReturn {
  onDeleteFile: (file: FileRecord, onSuccess: () => void) => Promise<void>;
  onDownloadFile: (file: FileRecord) => Promise<void>;
  onReprocessFile: (file: FileRecord, onSuccess: () => void) => Promise<void>;
}

// eslint-disable-next-line import/prefer-default-export
export const useFileActions = (): UseFileActionsReturn => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const onDeleteFile = async (file: FileRecord, onSuccess: () => void) => {
    try {
      const result = await confirm(
        `Are you sure you want to delete the file "${file.fileName}"? This action cannot be undone.`,
        {
          title: "Confirm File Deletion",
          okText: "Delete",
          cancelText: "Cancel",
          severity: "error",
        },
      );

      if (result === true) {
        await handleDeleteFile(file);
        showNotification({
          message: `File "${file.fileName}" deleted successfully.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (deleteError) {
      showNotification({
        message: `Failed to delete file. ${(deleteError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onDownloadFile = async (file: FileRecord) => {
    try {
      await handleDownloadFile(file);
      showNotification({
        message: `File "${file.fileName}" downloaded successfully.`,
        severity: "success",
        duration: 3000,
      });
    } catch (downloadError) {
      showNotification({
        message: `Failed to download file. ${(downloadError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  const onReprocessFile = async (file: FileRecord, onSuccess: () => void) => {
    try {
      const result = await confirm(
        React.createElement(
          Box,
          null,
          React.createElement(
            Typography,
            null,
            "Are you sure you want to reprocess the file:",
          ),
          React.createElement(
            Typography,
            { sx: { fontWeight: "bold", my: 2, textAlign: "center" } },
            file.fileName,
          ),
          React.createElement(
            Typography,
            { sx: { mb: 1 } },
            "This will reprocess the file from the beginning and should ",
            React.createElement("strong", null, "ONLY"),
            " be used when a batch was not created at all for this file.",
          ),
          React.createElement(
            Typography,
            null,
            "If a batch already exists, use the batch reprocessing functionality instead.",
          ),
        ),
        {
          title: "Confirm File Reprocessing",
          okText: "Reprocess",
          cancelText: "Cancel",
          severity: "warning",
        },
      );

      if (result === true) {
        await handleReprocessFile(file);
        showNotification({
          message: `File "${file.fileName}" is being reprocessed.`,
          severity: "success",
          duration: 3000,
        });
        onSuccess();
      }
    } catch (reprocessError) {
      showNotification({
        message: `Failed to reprocess file. ${(reprocessError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  };

  return {
    onDeleteFile,
    onDownloadFile,
    onReprocessFile,
  };
};
